import { useEffect, useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import { TCompanyMatch } from './companyData';
import { useGetCompanySearch } from '../../../../api/companySearch/useGetCompanySearch';

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

export type TCompanySearch = {
  query: string;
  setQuery: (query: string) => void;
  results: TCompanyMatch[];
  resultCount: number;
  isFetching: boolean;
  hasSearched: boolean;
  /** Settled searches so far — feeds enterCompanyManuallyClicked. */
  searchedCompaniesCount: number;
};

// Owns the register-search lifecycle for the company step. Lives in CompanyScreen
// (not CompanySearch) so the debounce, fetch and companySearchResponseShown
// tracking survive CompanySearch unmounting on select and remounting on deselect —
// otherwise the cached response would re-fire the event on every return to search.
export const useCompanySearch = (): TCompanySearch => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Mirrors desktop's counter: bumped once per settled search, so it tracks API
  // calls rather than keystrokes.
  const searchedCompaniesCount = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextQuery = query.length >= MIN_QUERY_LENGTH ? query : '';
      if (nextQuery) searchedCompaniesCount.current += 1;
      setDebouncedQuery(nextQuery);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isFetching, isError } = useGetCompanySearch({
    searchQuery: debouncedQuery,
    enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
  });

  const results = isError ? [] : (data?.data ?? []);
  const hasSearched = debouncedQuery.length >= MIN_QUERY_LENGTH;

  useEffect(() => {
    if (isFetching || !hasSearched || !data) return;
    tracking.ampli.companySearchResponseShown({
      query_length: debouncedQuery.length,
      response_length: data.data.length,
    });
  }, [data, isFetching, hasSearched, debouncedQuery]);

  return {
    query,
    setQuery,
    results,
    resultCount: results.length,
    isFetching,
    hasSearched,
    searchedCompaniesCount: searchedCompaniesCount.current,
  };
};
